import { useEffect, useState, useCallback } from 'react'
import { useLocation } from 'react-router'
import { mutate } from 'swr'

import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useNeedsAnalysisStatus } from '@/hooks/useNeedsAnalysisStatus'
import { useRole } from '@/hooks/useRole'
import { ClerkUserRole } from '@/types/auth'
import { getApiUrl } from '@/config/api'
import { isGuardExcludedPath } from '@/constants/routes'

import NeedsAnalysisModal from '../NeedsAnalysisModal'

type Props = {
  readonly children: React.ReactNode
}

export function NeedsAnalysisGuard({ children }: Props) {
  const location = useLocation()
  const { user, isLoading: isUserLoading } = useCurrentUser()
  const { hasCompletedNeedsAnalysis, isLoading: isStatusLoading, error: statusError } = useNeedsAnalysisStatus()
  const { userRole } = useRole()
  const [showModal, setShowModal] = useState(false)

  // Callback to handle when needs analysis modal closes after successful submission
  const handleModalClose = useCallback(
    (open: boolean) => {
      if (!open && showModal) {
        // Modal is closing - invalidate the recommendations cache to fetch fresh data
        // This ensures recommended courses appear immediately after completing needs analysis
        mutate(getApiUrl('/recommendations/courses'))
      }
      setShowModal(open)
    },
    [showModal]
  )

  const isLoading = isUserLoading || isStatusLoading

  useEffect(() => {
    if (isLoading || !user) return

    // If there's an error checking status (e.g. 500), we should probably let the user through
    // or show a retry. For now, let's log it and not block.
    if (statusError) {
      console.error('Failed to check needs analysis status:', statusError)
      return
    }

    // Only learners are automatically prompted with the Needs Analysis.
    // Other roles (admins, coaches, instructors, etc.) can still open it
    // from the user menu, but it should never auto-pop for them.
    if (userRole !== ClerkUserRole.LEARNER) {
      setShowModal(false)
      return
    }

    // Show modal for any learner who hasn't completed needs analysis
    if (!hasCompletedNeedsAnalysis) {
      setShowModal(true)
    } else {
      setShowModal(false)
    }
  }, [isLoading, user, hasCompletedNeedsAnalysis, statusError, userRole])

  const isOnExcludedPage = isGuardExcludedPath(location.pathname)

  return (
    <>
      {!isLoading && showModal && !isOnExcludedPage && (
        <NeedsAnalysisModal open={showModal} onOpenChange={handleModalClose} />
      )}
      {children}
    </>
  )
}
